// Section 24.12: the autosave ring (last N season snapshots plus one start-of-run save), named
// save slots and quota handling. Entries are encoded envelopes; small JSON indexes list them so
// the load screens never decode a save just to show its turn and date.
import { z } from 'zod';
import type { SaveEnvelope } from './envelope';
import { KEYS } from './keys';
import { decodeEnvelope, encodeEnvelope, isQuotaError, type StorageLike } from './save';
import { saveSummarySchema } from './schema';

export type SaveKind = 'autosave' | 'start' | 'slot';

const saveMetaSchema = z.strictObject({
  ...saveSummarySchema.shape,
  id: z.string().min(1),
  kind: z.enum(['autosave', 'start', 'slot']),
  name: z.string().nullable(),
  runId: z.string(),
  seed: z.string(),
  savedAt: z.string(),
});

export type SaveMeta = z.infer<typeof saveMetaSchema>;

/** `shrunkTo` is set when a quota error forced the ring down (the user must be told). */
export type WriteResult =
  | { ok: true; meta: SaveMeta; shrunkTo: number | null }
  | { ok: false; reason: 'quota' };

/** The ring size used after a quota error (Section 24.12). */
export const QUOTA_RING_SIZE = 2;

function readIndex(storage: StorageLike, key: string): SaveMeta[] {
  const text = storage.getItem(key);
  if (text === null) return [];
  try {
    const parsed = z.array(saveMetaSchema).safeParse(JSON.parse(text));
    return parsed.success ? parsed.data : [];
  } catch {
    return [];
  }
}

function writeIndex(storage: StorageLike, key: string, metas: SaveMeta[]): void {
  storage.setItem(key, JSON.stringify(metas));
}

function entryKey(meta: Pick<SaveMeta, 'kind' | 'id'>): string {
  return meta.kind === 'slot' ? KEYS.slot(meta.id) : KEYS.autosave(meta.id);
}

function metaOf(
  envelope: SaveEnvelope,
  kind: SaveKind,
  id: string,
  runId: string,
  name: string | null,
): SaveMeta {
  return {
    ...envelope.summary,
    id,
    kind,
    name,
    runId,
    seed: envelope.seed,
    savedAt: envelope.savedAt,
  };
}

/** Adopt the single M1 autosave into the ring the first time the ring is read. */
function adoptLegacy(storage: StorageLike): void {
  const text = storage.getItem(KEYS.legacyAutosave);
  if (text === null) return;
  storage.removeItem(KEYS.legacyAutosave);
  try {
    const envelope = decodeEnvelope(text);
    const meta = metaOf(envelope, 'autosave', `legacy-${envelope.summary.turn}`, 'legacy', null);
    storage.setItem(entryKey(meta), text);
    writeIndex(storage, KEYS.autosaveIndex, [meta]);
  } catch {
    // An unreadable legacy save is dropped; there is nothing to recover.
  }
}

/** Autosaves and the start-of-run save, newest first. */
export function listAutosaves(storage: StorageLike): SaveMeta[] {
  if (storage.getItem(KEYS.autosaveIndex) === null) adoptLegacy(storage);
  return readIndex(storage, KEYS.autosaveIndex);
}

/** Named save slots, newest first. */
export function listSlots(storage: StorageLike): SaveMeta[] {
  return readIndex(storage, KEYS.slotIndex);
}

/** Decode a listed save; null if it is missing or unreadable. */
export function readSave(storage: StorageLike, meta: Pick<SaveMeta, 'kind' | 'id'>): SaveEnvelope | null {
  const text = storage.getItem(entryKey(meta));
  if (text === null) return null;
  try {
    return decodeEnvelope(text);
  } catch {
    return null;
  }
}

/** The newest readable autosave (or the start save), for Continue and error recovery. */
export function latestAutosave(storage: StorageLike): { meta: SaveMeta; envelope: SaveEnvelope } | null {
  for (const meta of listAutosaves(storage)) {
    const envelope = readSave(storage, meta);
    if (envelope !== null) return { meta, envelope };
  }
  return null;
}

function removeEntries(storage: StorageLike, metas: readonly SaveMeta[]): void {
  for (const m of metas) storage.removeItem(entryKey(m));
}

/** Keep the newest `size` ring autosaves (the start save is not part of the ring). */
function pruneRing(storage: StorageLike, metas: SaveMeta[], size: number): SaveMeta[] {
  const ring = metas.filter((m) => m.kind === 'autosave');
  const drop = new Set(ring.slice(Math.max(0, size)).map((m) => m.id));
  removeEntries(
    storage,
    metas.filter((m) => m.kind === 'autosave' && drop.has(m.id)),
  );
  return metas.filter((m) => !(m.kind === 'autosave' && drop.has(m.id)));
}

/** Write text, shrinking the autosave ring (then dropping the start save) on quota errors. */
function writeWithQuota(
  storage: StorageLike,
  key: string,
  text: string,
): { ok: boolean; shrunkTo: number | null } {
  try {
    storage.setItem(key, text);
    return { ok: true, shrunkTo: null };
  } catch (error) {
    if (!isQuotaError(error)) throw error;
  }
  const attempts: ((metas: SaveMeta[]) => SaveMeta[])[] = [
    (metas) => pruneRing(storage, metas, QUOTA_RING_SIZE - 1),
    (metas) => {
      removeEntries(
        storage,
        metas.filter((m) => m.kind === 'start'),
      );
      return metas.filter((m) => m.kind !== 'start');
    },
  ];
  for (const shrink of attempts) {
    writeIndex(storage, KEYS.autosaveIndex, shrink(readIndex(storage, KEYS.autosaveIndex)));
    try {
      storage.setItem(key, text);
      return { ok: true, shrunkTo: QUOTA_RING_SIZE };
    } catch (error) {
      if (!isQuotaError(error)) throw error;
    }
  }
  return { ok: false, shrunkTo: QUOTA_RING_SIZE };
}

export interface AutosaveOptions {
  /** Identifies one run across its autosaves (ironman keeps only the run's newest). */
  runId: string;
  /** Ring size N from settings (default 8). */
  ringSize: number;
  /** 'start' is the one start-of-run save, overwritten by each new run. */
  kind?: 'autosave' | 'start';
}

/**
 * Section 4 and 24.12: write an autosave. The ring keeps the newest N season snapshots; an
 * ironman run keeps only its newest autosave and no start save.
 */
export function writeAutosave(
  storage: StorageLike,
  envelope: SaveEnvelope,
  opts: AutosaveOptions,
): WriteResult {
  const kind = opts.kind ?? 'autosave';
  const ironman = envelope.summary.ironman;
  const id = kind === 'start' ? 'start' : `${opts.runId}-t${envelope.summary.turn}`;
  const meta = metaOf(envelope, kind, id, opts.runId, null);
  const written = writeWithQuota(storage, entryKey(meta), encodeEnvelope(envelope));
  if (!written.ok) return { ok: false, reason: 'quota' };
  let metas = readIndex(storage, KEYS.autosaveIndex).filter(
    (m) => !(m.kind === kind && m.id === id),
  );
  if (ironman && kind === 'autosave') {
    const older = metas.filter((m) => m.runId === opts.runId);
    removeEntries(storage, older);
    metas = metas.filter((m) => m.runId !== opts.runId);
  }
  metas.unshift(meta);
  const size = written.shrunkTo ?? opts.ringSize;
  metas = pruneRing(storage, metas, size);
  writeIndex(storage, KEYS.autosaveIndex, metas);
  return { ok: true, meta, shrunkTo: written.shrunkTo };
}

/** Apply a new ring size from settings. */
export function resizeRing(storage: StorageLike, size: number): void {
  writeIndex(storage, KEYS.autosaveIndex, pruneRing(storage, listAutosaves(storage), size));
}

function slotId(name: string, existing: readonly SaveMeta[]): string {
  const same = existing.find((m) => m.name === name);
  if (same !== undefined) return same.id;
  let n = existing.length + 1;
  while (existing.some((m) => m.id === `s${n}`)) n++;
  return `s${n}`;
}

/** Save to a named slot; a slot with the same name is overwritten. */
export function writeSlot(
  storage: StorageLike,
  name: string,
  envelope: SaveEnvelope,
  runId: string,
): WriteResult {
  const existing = listSlots(storage);
  const meta = metaOf(envelope, 'slot', slotId(name, existing), runId, name);
  const written = writeWithQuota(storage, entryKey(meta), encodeEnvelope(envelope));
  if (!written.ok) return { ok: false, reason: 'quota' };
  writeIndex(storage, KEYS.slotIndex, [meta, ...existing.filter((m) => m.id !== meta.id)]);
  return { ok: true, meta, shrunkTo: written.shrunkTo };
}

export function deleteSave(storage: StorageLike, meta: Pick<SaveMeta, 'kind' | 'id'>): void {
  storage.removeItem(entryKey(meta));
  const key = meta.kind === 'slot' ? KEYS.slotIndex : KEYS.autosaveIndex;
  writeIndex(
    storage,
    key,
    readIndex(storage, key).filter((m) => !(m.kind === meta.kind && m.id === meta.id)),
  );
}
